import React, { useState } from "react";
import {
  SEND_NEW_INVITE,
  SEND_TEMP_PASS,
} from "../../../../utils/GraphQL/mutations";
import { useSnackbar } from "../../../../utils/GlobalSnackbar/GlobalSnackbarContext";
import "./inviteUser.css";
import BdrModal from "../../../../components/BdrModal/BdrModal";
import BdrButton from "../../../../components/BdrButton/BdrButton";
import BdrInput from "../../../../components/BdrInput/BdrInput";
import capitalizeFirstLetters from "../../../../utils/formatters/capitalizeFirstLetters";
import { useMutation } from "@apollo/client";

const InviteNewUser = ({ onClose }) => {
  const { handleSnack } = useSnackbar();
  const [userExists, setUserExists] = useState(false);
  const [resetSentSuccess, setResetSentSuccess] = useState(false);
  const [userInviteMes, setUserInviteMes] = useState("");
  const [sendInviteEmail] = useMutation(SEND_NEW_INVITE);
  const [sendTempPassReset] = useMutation(SEND_TEMP_PASS);
  const [inviteForm, setInviteForm] = useState({
    emailToInvite: "",
    firstName: "",
    permission: "",
  });

  const handleInviteForm = (event) => {
    const name = event.target.name;
    let value = event.target.value;

    if (name === "firstName") {
      value = capitalizeFirstLetters(value);
    }

    if (name === "emailToInvite") {
      value = value.toLowerCase();
    }

    setInviteForm((prev) => {
      return { ...prev, [name]: value };
    });
  };

  const autoEmailAdd = (event) => {
    const atDomain = event.target.innerText;
    const email = inviteForm?.emailToInvite.split("@")[0];

    setInviteForm((prev) => {
      return { ...prev, emailToInvite: `${email}${atDomain}` };
    });
  };

  const changePerm = (event) => {
    const value = event.target.name;
    setInviteForm((prev) => {
      return { ...prev, permission: value };
    });
  };

  const sendInvite = async () => {
    const emailRegex = /.+@.+\..+/;

    const Email = emailRegex.test(inviteForm?.emailToInvite);
    const Perm = inviteForm.permission?.length > 0;
    const First = inviteForm.firstName?.length > 0;

    if (Email && Perm && First) {
      const { data } = await sendInviteEmail({
        variables: { ...inviteForm },
      });

      if (data?.sendInviteEmail === null) {
        setUserExists(true);
        setUserInviteMes("User Already Exists - Send Password Reset?");
        return;
      }

      setInviteForm({
        emailToInvite: "",
        firstName: "",
        permission: "",
      });

      handleSnack("Invite Sent", 2000);
      onClose && onClose();
    }
  };

  const sendTempPass = async () => {
    const { data: tempData } = await sendTempPassReset({
      variables: { emailTo: inviteForm.emailToInvite },
    });

    if (tempData?.sendForgotPasswordEmail === true) {
      setUserInviteMes("Password Reset Email Sent");
      setResetSentSuccess(true);
      return;
    }

    setUserInviteMes("Error - Email Not Sent");
    setResetSentSuccess(false);
  };

  const emailValid = /.+@.+\..+/.test(inviteForm.emailToInvite);
  const canSend =
    inviteForm.firstName.length > 0 &&
    inviteForm.permission.length > 0 &&
    emailValid;

  return (
    <BdrModal
      open
      onClose={onClose}
      size="sm"
      variant="panel"
      title="Invite New User"
      actions={
        <>
          <BdrButton onClick={onClose}>Cancel</BdrButton>
          <BdrButton
            variant="confirm-solid"
            onClick={sendInvite}
            disabled={!canSend}
          >
            Send Invite
          </BdrButton>
        </>
      }
    >
      <BdrInput
        type="text"
        label="First Name"
        name="firstName"
        value={inviteForm.firstName}
        onChange={handleInviteForm}
        autoFocus
      />

      {/* Three mutually exclusive choices shown as buttons rather than a
          dropdown, because seeing all three at once IS the point on a
          permission field. `action` for the unselected ones and `confirm` for
          the chosen one, so the selection reads without a colour of its own. */}
      <div className="bdr-field">
        <span className="bdr-field-label">Permission</span>
        <div className="iu-perm-row">
          {["admin", "office", "refinisher"].map((perm) => (
            <BdrButton
              key={perm}
              size="sm"
              name={perm}
              variant={inviteForm.permission === perm ? "confirm" : "default"}
              onClick={changePerm}
            >
              {perm.charAt(0).toUpperCase() + perm.slice(1)}
            </BdrButton>
          ))}
        </div>
      </div>

      <BdrInput
        type="text"
        label="Email"
        name="emailToInvite"
        value={inviteForm.emailToInvite}
        onChange={handleInviteForm}
        /* The suffix shortcuts belong to this field, so they sit under it as
           its helper row rather than floating as a separate block. */
        helper="Tap a suffix to append it"
      />

      <div className="iu-suffix-row">
        {["@gmail.com", "@yahoo.com", "@icloud.com"].map((suffix) => (
          <BdrButton key={suffix} size="sm" onClick={autoEmailAdd}>
            {suffix}
          </BdrButton>
        ))}
      </div>

      {userExists && (
        /* This one changes meaning as it is used: it offers to send a
           temporary password, then reports that it did. `confirm` once it has
           succeeded, so the success is the button's own state rather than a
           separate message. */
        <BdrButton
          variant={resetSentSuccess ? "confirm" : "action"}
          className="iu-exists-btn"
          onClick={sendTempPass}
        >
          {userInviteMes}
        </BdrButton>
      )}
    </BdrModal>
  );
};

export default InviteNewUser;
